import axios from "axios";
import type { ColumnDef } from "./quote-invoice-columns";

// export const BASE_URL = "http://localhost:3000/api/v1/";
export const BASE_URL = "https://www.rentops.service.waposcaffolding.com/api/v1/";
export const API_KEY =
  "3f201c17628e67c3709a435be20090bb2249b19b8856cfe31e145b91ddb3249b";

export interface RequestOtpResponse {
  message: string;
}

export interface User {
  id: string;
  fullName: string;
  email: string;
  phoneNumber: string;
  role: string;
  status: string;
  street: string | null;
  ward: string | null;
  district: string | null;
  region: string | null;
  country: string | null;
}

export interface VerifyOtpResponse {
  accessToken: string;
  user: User;
}

// Stats Interfaces
export interface CustomerStatistics {
  totalCustomers: number;
  byType: {
    individual: number;
    organization: number;
  };
  totals: {
    sites: number;
    quotations: number;
    invoices: number;
  };
  averages: {
    sitesPerCustomer: number;
    quotationsPerCustomer: number;
    invoicesPerCustomer: number;
  };
  byRegion: Array<{
    region: string;
    count: number;
  }>;
}

// Customer Interfaces
export interface CustomerSite {
  id: string;
  name: string;
  street: string;
  ward: string;
  district: string;
  region: string;
  country: string;
  status: "ACTIVE" | "INACTIVE";
  customerId: string;
  createdAt: string;
  updatedAt: string;
}

export interface CustomerQuotation {
  id: string;
  reference: string;
  issueDate: string;
  expiryDate: string;
  currencyType: string;
  amount: string;
  vat: string;
  totalAmount: string;
  isVatIncluded: boolean;
  showIsVatExempted?: boolean;
  status: string;
  customerId: string;
  siteId: string;
  title?: string;
  notes?: string;
  /** Introductory text above the items table (e.g. "We are pleased to quote you for the following"). */
  description?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CustomerInvoice {
  id: string;
  reference: string;
  issueDate: string;
  dueDate: string;
  currencyType: string;
  amount: string;
  vat: string;
  totalAmount: string;
  isVatIncluded: boolean;
  showIsVatExempted?: boolean;
  status: string;
  customerId: string;
  siteId: string;
  title?: string;
  notes?: string;
  /** Introductory text above the items table (e.g. "We are pleased to quote you for the following"). */
  description?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Customer {
  id: string;
  fullName: string;
  phoneNumber: string;
  type: "ORGANIZATION" | "INDIVIDUAL";
  pointOfContactName: string;
  pointOfContactPhone: string;
  street: string;
  ward: string;
  district: string;
  region: string;
  country: string;
  postalCode: string;
  tin: string;
  vrn: string;
  createdAt: string;
  updatedAt: string;
  sites?: CustomerSite[];
  quotations?: CustomerQuotation[];
  invoices?: CustomerInvoice[];
}

export interface CreateCustomerPayload {
  fullName: string;
  phoneNumber: string;
  type: "ORGANIZATION" | "INDIVIDUAL";
  pointOfContactName: string;
  pointOfContactPhone: string;
  street: string;
  ward: string;
  district: string;
  region: string;
  country: string;
  postalCode?: string;
  tin?: string;
  vrn?: string;
}

export interface GetCustomersResponse {
  data: Customer[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

// Helper to get cookie value
const getCookie = (name: string): string | null => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(";").shift() || null;
  return null;
};

const api = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "application/json",
    "x-api-key": API_KEY,
  },
});

// Request interceptor to add Bearer token
api.interceptors.request.use(
  (config) => {
    const accessToken = getCookie("accessToken");
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

let isHandlingUnauthorized = false;

const logoutOnUnauthorized = () => {
  if (isHandlingUnauthorized) return;
  isHandlingUnauthorized = true;

  // Match `useUser().logout()` behavior
  document.cookie = "accessToken=; path=/; max-age=0; SameSite=Strict";
  document.cookie = "user=; path=/; max-age=0; SameSite=Strict";

  // If we're already on login page, no need to redirect.
  if (window.location.pathname !== "/login") {
    window.location.replace("/login");
  }
};

// Response interceptor to logout on 401
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      logoutOnUnauthorized();
    }
    return Promise.reject(error);
  },
);

// Helper to handle axios errors
const handleAxiosError = (error: unknown, defaultMessage: string): never => {
  if (axios.isAxiosError(error)) {
    const message =
      error.response?.data?.message || error.message || defaultMessage;
    throw new Error(message);
  }
  throw new Error(defaultMessage);
};

export const requestOtp = async (
  identifier: string,
): Promise<RequestOtpResponse> => {
  try {
    const response = await api.post<RequestOtpResponse>("auth/request-otp", {
      identifier,
    });
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to send OTP");
    throw error;
  }
};

export const verifyOtp = async (
  identifier: string,
  otp: string,
): Promise<VerifyOtpResponse> => {
  try {
    const response = await api.post<VerifyOtpResponse>("auth/login", {
      identifier,
      otp,
    });
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to verify OTP");
    throw error;
  }
};

// Customer APIs
export const createCustomer = async (
  data: CreateCustomerPayload,
): Promise<Customer> => {
  try {
    const response = await api.post<Customer>("customers", data);
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to create customer");
    throw error;
  }
};

export const getCustomers = async (
  page = 1,
  limit = 10,
  search = "",
  type?: "INDIVIDUAL" | "ORGANIZATION",
): Promise<GetCustomersResponse> => {
  try {
    const params: { page: number; limit: number; search: string; type?: string } = {
      page,
      limit,
      search,
    };
    if (type) params.type = type;
    const response = await api.get<GetCustomersResponse>("customers", {
      params,
    });
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to get customers");
    throw error;
  }
};

export const getCustomerMovements = async (
  customerId: string,
  page = 1,
  limit = 10,
  activitySubType?: string,
  status?: string,
): Promise<GetMovementActivitiesResponse> => {
  try {
    const params: {
      page: number;
      limit: number;
      activitySubType?: string;
      status?: string;
    } = { page, limit };
    if (activitySubType) params.activitySubType = activitySubType;
    if (status) params.status = status;
    const response = await api.get<GetMovementActivitiesResponse>(
      `customers/${customerId}/movements`,
      { params },
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to get customer movements");
    throw error;
  }
};

export const getCustomer = async (id: string): Promise<Customer> => {
  try {
    const response = await api.get<Customer>(`customers/${id}`);
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to get customer details");
    throw error;
  }
};

export const updateCustomer = async (
  id: string,
  data: Partial<CreateCustomerPayload>,
): Promise<Customer> => {
  try {
    const response = await api.patch<Customer>(`customers/${id}`, data);
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to update customer");
    throw error;
  }
};

export const getCustomerStatistics = async (): Promise<CustomerStatistics> => {
  try {
    const response = await api.get<CustomerStatistics>("customers/statistics");
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to get customer statistics");
    throw error;
  }
};

// Site Interfaces
export interface Site {
  id: string;
  name: string;
  street: string;
  ward: string;
  district: string;
  region: string;
  country: string;
  status: "ACTIVE" | "INACTIVE";
  customerId: string;
  createdAt: string;
  updatedAt: string;
  customer?: Customer;
  quotations?: CustomerQuotation[];
  invoices?: CustomerInvoice[];
}

export interface SiteRemarkUser {
  id: string;
  fullName: string;
  email: string;
}

export interface SiteRemark {
  id: string;
  siteId: string;
  text: string;
  createdAt: string;
  user?: SiteRemarkUser;
}

export interface OverdueDeliverySite extends Site {
  overdueSince: string;
  remarks: SiteRemark[];
}

export interface CreateSitePayload {
  customerId: string;
  name: string;
  street: string;
  ward: string;
  district: string;
  region: string;
  country: string;
  status: "ACTIVE" | "INACTIVE";
}

export interface GetSitesResponse {
  data: Site[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface SiteStatistics {
  totalSites: number;
  byStatus: {
    active: number;
    inactive: number;
    breakdown: Array<{
      status: string;
      count: number;
    }>;
  };
  totals: {
    quotations: number;
    invoices: number;
  };
  averages: {
    quotationsPerSite: number;
    invoicesPerSite: number;
  };
  byRegion: Array<{
    region: string;
    count: number;
  }>;
  topCustomers: Array<{
    customerId: string;
    customerName: string;
    siteCount: number;
  }>;
}

// Site APIs
export const createSite = async (data: CreateSitePayload): Promise<Site> => {
  try {
    const response = await api.post<Site>("sites", data);
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to create site");
    throw error;
  }
};

export const getSites = async (
  page = 1,
  limit = 10,
  search = "",
  hasOverdueDeliveries?: boolean,
  status?: "ACTIVE" | "INACTIVE",
  region?: string,
): Promise<GetSitesResponse> => {
  try {
    const params: {
      page: number;
      limit: number;
      search: string;
      hasOverdueDeliveries?: boolean;
      status?: "ACTIVE" | "INACTIVE";
      region?: string;
    } = {
      page,
      limit,
      search,
    };
    if (hasOverdueDeliveries === true) params.hasOverdueDeliveries = true;
    if (status) params.status = status;
    if (region?.trim()) params.region = region.trim();
    const response = await api.get<GetSitesResponse>("sites", { params });
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to get sites");
    throw error;
  }
};

export const getSite = async (id: string): Promise<Site> => {
  try {
    const response = await api.get<Site>(`sites/${id}`);
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to get site details");
    throw error;
  }
};

export const updateSite = async (
  id: string,
  data: Partial<CreateSitePayload>,
): Promise<Site> => {
  try {
    const response = await api.patch<Site>(`sites/${id}`, data);
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to update site");
    throw error;
  }
};

export const getSiteStatistics = async (): Promise<SiteStatistics> => {
  try {
    const response = await api.get<SiteStatistics>("sites/statistics");
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to get site statistics");
    throw error;
  }
};

export const getOverdueDeliverySites = async (): Promise<OverdueDeliverySite[]> => {
  try {
    const response = await api.get<OverdueDeliverySite[]>("sites/overdue");
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to get overdue delivery sites");
    throw error;
  }
};

export const getSiteRemarks = async (siteId: string): Promise<SiteRemark[]> => {
  try {
    const response = await api.get<SiteRemark[]>(`sites/${siteId}/remarks`);
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to get site remarks");
    throw error;
  }
};

export const addSiteRemark = async (
  siteId: string,
  text: string,
): Promise<SiteRemark[]> => {
  try {
    const response = await api.post<SiteRemark[]>(`sites/${siteId}/remarks`, {
      text,
    });
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to add site remark");
    throw error;
  }
};

export const updateSiteRemark = async (
  siteId: string,
  remarkId: string,
  text: string,
): Promise<SiteRemark[]> => {
  try {
    const response = await api.patch<SiteRemark[]>(
      `sites/${siteId}/remarks/${remarkId}`,
      { text },
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to update site remark");
    throw error;
  }
};

// Site Details Statistics Interface
export interface SiteDetailsStatistics {
  site: {
    id: string;
    name: string;
    status: string;
    region: string;
    district: string;
    ward: string;
    country: string;
    customerId: string;
    customerName: string;
  };
  quotations: {
    count: number;
    totalAmount: number;
    byStatus: Array<{
      status: string;
      count: number;
      totalAmount: number;
    }>;
  };
  invoices: {
    count: number;
    totalAmount: number;
    paid: number;
    unpaid: number;
    byStatus: Array<{
      status: string;
      count: number;
      totalAmount: number;
    }>;
  };
  movements: {
    total: number;
    asSource: number;
    asDestination: number;
    closed: number;
    totalQuantity: number;
    byStatus: Array<{
      status: string;
      count: number;
      quantity: number;
    }>;
    byActivityType: Array<{
      activityType: string;
      count: number;
      quantity: number;
    }>;
    byActivitySubType: Array<{
      activitySubType: string;
      count: number;
      quantity: number;
    }>;
  };
}

export const getSiteDetailsStatistics = async (
  siteId: string,
): Promise<SiteDetailsStatistics> => {
  try {
    const response = await api.get<SiteDetailsStatistics>(
      `sites/${siteId}/statistics`,
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to get site details statistics");
    throw error;
  }
};

// Inventory Interfaces
export interface Inventory {
  id: string;
  itemCode: string;
  description: string;
  unitWeight: string | number;
  length: string;
  availableQuantity: number;
  damagedQuantity: number;
  rentedQuantity: number;
  reservedQuantity: number;
  lostQuantity: number;
  soldQuantity: number;
  totalQuantity: number;
  value: number;
  hireRatePerWeek: number;
  totalValue?: number;
  warehouseId: string;
  createdAt: string;
  updatedAt: string;
  warehouse?: Warehouse;
}

export interface CreateInventoryPayload {
  warehouseId: string;
  itemCode: string;
  description: string;
  unitWeight: number;
  length: string;
  availableQuantity: number;
  damagedQuantity: number;
  rentedQuantity: number;
  reservedQuantity: number;
  lostQuantity: number;
  soldQuantity?: number;
  value?: number;
  hireRatePerWeek?: number;
}

export interface BulkUpsertInventoriesPayload {
  items: CreateInventoryPayload[];
}

export interface BulkUpsertInventoriesResponse {
  createdCount: number;
  updatedCount: number;
  created: Inventory[];
  updated: Inventory[];
}

export interface GetInventoriesResponse {
  data: Inventory[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface InventoryStatistics {
  totalItems: number;
  totals: {
    available: number;
    damaged: number;
    rented: number;
    reserved: number;
    lost: number;
    sold: number;
    total: number;
    value: number;
  };
  averages: {
    availablePerItem: number;
    damagedPerItem: number;
    rentedPerItem: number;
    reservedPerItem: number;
    lostPerItem: number;
    soldPerItem: number;
    totalPerItem: number;
  };
  stockStatus: {
    lowStock: number;
    outOfStock: number;
    inStock: number;
  };
  byWarehouse: Array<{
    warehouseId: string;
    warehouseName: string;
    itemCount: number;
    totals: {
      available: number;
      damaged: number;
      rented: number;
      reserved: number;
      lost: number;
      sold: number;
      total: number;
      value: number;
    };
  }>;
}

// Warehouse Interfaces

export interface Warehouse {
  id: string;
  name: string;
  street: string;
  ward: string;
  district: string;
  region: string;
  country: string;
  pointOfContactName: string;
  pointOfContactPhone: string;
  status: "ACTIVE" | "INACTIVE";
  createdAt: string;
  updatedAt: string;
  inventories?: Inventory[];
  totalAvailableWeight?: number;
  totalDamagedWeight?: number;
  totalRentedWeight?: number;
  totalReservedWeight?: number;
  totalLostWeight?: number;
  totalSoldWeight?: number;
  totalWeight?: number;
  totalValue?: number;
}

export interface CreateWarehousePayload {
  name: string;
  street: string;
  ward: string;
  district: string;
  region: string;
  country: string;
  pointOfContactName: string;
  pointOfContactPhone: string;
  status: "ACTIVE" | "INACTIVE";
}

export interface GetWarehousesResponse {
  data: Warehouse[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface WarehouseStatistics {
  totalWarehouses: number;
  byStatus: {
    active: number;
    inactive: number;
    breakdown: Array<{
      status: string;
      count: number;
    }>;
  };
  totals: {
    inventoryItems: number;
    available: number;
    damaged: number;
    rented: number;
    reserved: number;
    lost: number;
    sold: number;
    total: number;
    value: number;
  };
  averages: {
    itemsPerWarehouse: number;
    availablePerWarehouse: number;
    rentedPerWarehouse: number;
  };
  byRegion: Array<{
    region: string;
    count: number;
  }>;
  warehouses: Array<{
    warehouseId: string;
    warehouseName: string;
    status: string;
    region: string;
    itemCount: number;
    totals: {
      available: number;
      damaged: number;
      rented: number;
      reserved: number;
      lost: number;
      sold: number;
      total: number;
      value: number;
    };
  }>;
}

// Warehouse APIs
export const createWarehouse = async (
  data: CreateWarehousePayload,
): Promise<Warehouse> => {
  try {
    const response = await api.post<Warehouse>("warehouses", data);
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to create warehouse");
    throw error;
  }
};

export const getWarehouses = async (
  page = 1,
  limit = 10,
  search = "",
  status?: string,
): Promise<GetWarehousesResponse> => {
  try {
    const params: { page: number; limit: number; search: string; status?: string } = {
      page,
      limit,
      search,
    };
    if (status) params.status = status;
    const response = await api.get<GetWarehousesResponse>("warehouses", {
      params,
    });
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to get warehouses");
    throw error;
  }
};

export const getWarehouse = async (id: string): Promise<Warehouse> => {
  try {
    const response = await api.get<Warehouse>(`warehouses/${id}`);
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to get warehouse details");
    throw error;
  }
};

export const updateWarehouse = async (
  id: string,
  data: Partial<CreateWarehousePayload>,
): Promise<Warehouse> => {
  try {
    const response = await api.patch<Warehouse>(`warehouses/${id}`, data);
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to update warehouse");
    throw error;
  }
};

export const getWarehouseStatistics =
  async (): Promise<WarehouseStatistics> => {
    try {
      const response = await api.get<WarehouseStatistics>(
        "warehouses/statistics",
      );
      return response.data;
    } catch (error) {
      handleAxiosError(error, "Failed to get warehouse statistics");
      throw error;
    }
  };

// Inventory APIs
export const createInventory = async (
  data: CreateInventoryPayload,
): Promise<Inventory> => {
  try {
    const response = await api.post<Inventory>("inventories", data);
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to create inventory item");
    throw error;
  }
};

export const bulkUpsertInventories = async (
  data: BulkUpsertInventoriesPayload,
): Promise<BulkUpsertInventoriesResponse> => {
  try {
    const response = await api.post<BulkUpsertInventoriesResponse>(
      "inventories/bulk",
      data,
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to upload inventories in bulk");
    throw error;
  }
};

export const getInventories = async (
  page = 1,
  limit = 10,
  search = "",
  warehouseId?: string,
): Promise<GetInventoriesResponse> => {
  try {
    const params: any = { page, limit, search };
    if (warehouseId) {
      params.warehouseId = warehouseId;
    }
    const response = await api.get<GetInventoriesResponse>("inventories", {
      params,
    });
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to get inventories");
    throw error;
  }
};

export const getInventory = async (id: string): Promise<Inventory> => {
  try {
    const response = await api.get<Inventory>(`inventories/${id}`);
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to get inventory details");
    throw error;
  }
};

export const updateInventory = async (
  id: string,
  data: Partial<CreateInventoryPayload>,
): Promise<Inventory> => {
  try {
    const response = await api.patch<Inventory>(`inventories/${id}`, data);
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to update inventory");
    throw error;
  }
};

export const getInventoryStatistics = async (
  warehouseId?: string,
): Promise<InventoryStatistics> => {
  try {
    const response = await api.get<InventoryStatistics>(
      "inventories/statistics",
      { params: warehouseId ? { warehouseId } : {} },
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to get inventory statistics");
    throw error;
  }
};

// Vendor Interfaces
export interface Vendor {
  id: string;
  fullName: string;
  phoneNumber: string;
  email: string;
  address: string;
  country: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateVendorPayload {
  fullName: string;
  phoneNumber: string;
  email: string;
  address: string;
  country: string;
}

export interface GetVendorsResponse {
  data: Vendor[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface VendorStatistics {
  totalVendors: number;
  totals: {
    movements: number;
    items: number;
    quantity: number;
  };
  averages: {
    movementsPerVendor: number;
    itemsPerVendor: number;
    quantityPerVendor: number;
  };
  byCountry: Array<{
    country: string;
    count: number;
  }>;
  topVendors: Array<{
    vendorId: string;
    vendorName: string;
    totalMovements: number;
    totalItems: number;
    totalQuantity: number;
  }>;
  vendors: Array<{
    vendorId: string;
    vendorName: string;
    country: string;
    totalMovements: number;
    totalItems: number;
    totalQuantity: number;
  }>;
}

// Vendor APIs
export const createVendor = async (
  data: CreateVendorPayload,
): Promise<Vendor> => {
  try {
    const response = await api.post<Vendor>("vendors", data);
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to create vendor");
    throw error;
  }
};

export const getVendors = async (
  page = 1,
  limit = 10,
  search = "",
): Promise<GetVendorsResponse> => {
  try {
    const response = await api.get<GetVendorsResponse>("vendors", {
      params: { page, limit, search },
    });
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to get vendors");
    throw error;
  }
};

export const getVendor = async (id: string): Promise<Vendor> => {
  try {
    const response = await api.get<Vendor>(`vendors/${id}`);
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to get vendor details");
    throw error;
  }
};

export const updateVendor = async (
  id: string,
  data: Partial<CreateVendorPayload>,
): Promise<Vendor> => {
  try {
    const response = await api.patch<Vendor>(`vendors/${id}`, data);
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to update vendor");
    throw error;
  }
};

export const getVendorStatistics = async (): Promise<VendorStatistics> => {
  try {
    const response = await api.get<VendorStatistics>("vendors/statistics");
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to get vendor statistics");
    throw error;
  }
};

// User Management Interfaces
export interface UserManagement {
  id: string;
  fullName: string;
  phoneNumber: string;
  email: string;
  role:
    | "ADMIN"
    | "OPERATION"
    | "WAREHOUSE"
    | "SALES"
    | "ACCOUNTANT"
    | "MANAGEMENT";
  status: "ACTIVE" | "DISABLED";
  street: string | null;
  ward: string | null;
  district: string | null;
  region: string | null;
  country: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateUserPayload {
  fullName: string;
  phoneNumber: string;
  email: string;
  role:
  | "ADMIN"
  | "OPERATION"
  | "WAREHOUSE"
  | "SALES"
  | "ACCOUNTANT"
  | "MANAGEMENT";
  street?: string;
  ward?: string;
  district?: string;
  region?: string;
  country?: string;
}

export interface UpdateUserPayload {
  fullName: string;
  phoneNumber: string;
  email: string;
  role:
  | "ADMIN"
  | "OPERATION"
  | "WAREHOUSE"
  | "SALES"
  | "ACCOUNTANT"
  | "MANAGEMENT";
  status: "ACTIVE" | "DISABLED";
  street?: string;
  ward?: string;
  district?: string;
  region?: string;
  country?: string;
}

export interface GetUsersResponse {
  data: UserManagement[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface UserStatistics {
  totalUsers: number;
  byStatus: {
    active: number;
    disabled: number;
    breakdown: Array<{
      status: string;
      count: number;
    }>;
  };
  byRole: Array<{
    role: string;
    count: number;
  }>;
  activity: {
    totalMovementsInitiated: number;
    averageMovementsPerUser: number;
    topActiveUsers: Array<{
      userId: string;
      userName: string;
      movementsInitiated: number;
    }>;
  };
  monthlyTrend: Array<{
    month: string;
    count: number;
  }>;
}

// User Management APIs
export const createUser = async (
  data: CreateUserPayload,
): Promise<UserManagement> => {
  try {
    const response = await api.post<UserManagement>("users", data);
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to create user");
    throw error;
  }
};

export const getUsers = async (
  page = 1,
  limit = 10,
  search = "",
  role?: string,
): Promise<GetUsersResponse> => {
  try {
    const params: { page: number; limit: number; search: string; role?: string } = {
      page,
      limit,
      search,
    };
    if (role) params.role = role;
    const response = await api.get<GetUsersResponse>("users", {
      params,
    });
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to get users");
    throw error;
  }
};

export const getUser = async (id: string): Promise<UserManagement> => {
  try {
    const response = await api.get<UserManagement>(`users/${id}`);
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to get user details");
    throw error;
  }
};

export const updateUser = async (
  id: string,
  data: Partial<UpdateUserPayload>,
): Promise<UserManagement> => {
  try {
    const response = await api.patch<UserManagement>(`users/${id}`, data);
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to update user");
    throw error;
  }
};

export const getUserStatistics = async (): Promise<UserStatistics> => {
  try {
    const response = await api.get<UserStatistics>("users/statistics");
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to get user statistics");
    throw error;
  }
};

// Activity Logs
export interface ActivityLogUser {
  id: string;
  fullName: string;
  email: string;
  phoneNumber: string;
  role: string;
  status: string;
}

export interface ActivityLog {
  id: string;
  userId: string;
  action: string;
  entityType: string;
  entityId: string | null;
  details: Record<string, unknown> | null;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
  user: ActivityLogUser | null;
}

export interface ActivityLogsListResponse {
  data: ActivityLog[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface ActivityLogsQuery {
  page?: number;
  limit?: number;
  search?: string;
  userId?: string;
  action?: string;
  entityType?: string;
  entityId?: string;
  dateFrom?: string;
  dateTo?: string;
}

export const getActivityLogs = async (
  query: ActivityLogsQuery = {},
): Promise<ActivityLogsListResponse> => {
  try {
    const params: Record<string, string | number> = {
      page: query.page ?? 1,
      limit: query.limit ?? 10,
    };
    if (query.search) params.search = query.search;
    if (query.userId) params.userId = query.userId;
    if (query.action) params.action = query.action;
    if (query.entityType) params.entityType = query.entityType;
    if (query.entityId) params.entityId = query.entityId;
    if (query.dateFrom) params.dateFrom = query.dateFrom;
    if (query.dateTo) params.dateTo = query.dateTo;
    const response = await api.get<ActivityLogsListResponse>("activity-logs", {
      params,
    });
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to get activity logs");
    throw error;
  }
};

// Movement Activities Interfaces
/** Project type enum; values match backend ProjectType. */
export const ProjectType = {
  SCAFFOLDING: "SCAFFOLDING",
  FORMWORK: "FORMWORK",
  TRUSSWORK: "TRUSSWORK",
  PAINTING: "PAINTING",
  SANDBLASTING_AND_PAINTING: "SANDBLASTING & PAINTING",
  SCISSORS_LIFT_MACHINE: "SCISSORS LIFT MACHINE",
  CRADLE_LIFT_MACHINE: "CRADLE LIFT MACHINE",
  ROUGH_MATERIALS: "ROUGH MATERIALS",
  SCAFFOLDING_TRAINING: "SCAFFOLDING TRAINING",
} as const;

export type ProjectTypeValue = (typeof ProjectType)[keyof typeof ProjectType];

export interface MovementActivity {
  id: string;
  reference: string;
  initiatorId: string;
  sourceSiteId: string | null;
  sourceWarehouseId: string | null;
  sourceVendorId: string | null;
  destinationSiteId: string | null;
  destinationWarehouseId: string | null;
  destinationCustomerId: string | null;
  activityType: string;
  activitySubType: string;
  projectType?: string | null;
  plannedDate: string;
  notes: string;
  daysHired?: number;
  days_hired?: number;
  driverName?: string;
  driver_name?: string;
  driverPhoneNumber?: string;
  driver_phone_number?: string;
  vehicleNumber?: string;
  vehicle_number?: string;
  returnedBy?: string;
  returned_by?: string;
  receivedBy?: string;
  received_by?: string;
  approvedBy?: string;
  approved_by?: string;
  returnedDate?: string;
  returned_date?: string;
  status: string;
  statusChangedBy: string | null;
  statusChangedAt: string | null;
  createdAt: string;
  updatedAt: string;
  initiator: UserManagement;
  sourceSite: Site | null;
  sourceWarehouse: Warehouse | null;
  sourceVendor: Vendor | null;
  destinationSite: Site | null;
  destinationWarehouse: Warehouse | null;
  destinationCustomer: Customer | null;
  items: Array<{
    id: string;
    particulars: string;
    totalQuantity: number;
    goodQuantity: number;
    damagedQuantity: number;
    missedQuantity?: number;
    missed_quantity?: number;
    inventoryId: string;
    movementId: string;
    inventory: Inventory;
    reason?: string;
  }>;
  documents: Array<{
    id: string;
    fileName: string;
    filePath: string | null;
    mimeType: string;
    movementId: string;
    uploadedAt: string;
  }>;
  statusChangedByUser: UserManagement | null;
  /** Linked invoices (zero or more). */
  invoices?: Invoice[];
  statusHistory: Array<{
    id: string;
    movementId: string;
    previousStatus: string;
    newStatus: string;
    changedBy: string;
    changedAt: string;
    notes: string;
    changedByUser: UserManagement;
  }>;
  daysHiredExtensions?: Array<{
    id: string;
    movementId: string;
    dateFrom: string;
    dateTo: string;
    daysAdded: number;
    previousDaysHired: number;
    newDaysHired: number;
    notes: string | null;
    extendedBy: string;
    createdAt: string;
    extendedByUser?: UserManagement;
  }>;
}

export interface GetMovementActivitiesResponse {
  data: MovementActivity[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface MovementActivityStatistics {
  totalMovements: number;
  closedMovements: number;
  cancelledMovements: number;
  totals: {
    items: number;
    quantity: number;
    goodQuantity: number;
    damagedQuantity: number;
  };
  averages: {
    itemsPerMovement: number;
    quantityPerMovement: number;
  };
  byStatus: Array<{
    status: string;
    count: number;
  }>;
  byActivityType: Array<{
    activityType: string;
    count: number;
  }>;
  byActivitySubType: Array<{
    activitySubType: string;
    count: number;
  }>;
  byTypeAndSubType: Array<{
    activityType: string;
    activitySubType: string;
    count: number;
  }>;
  monthlyTrend: Array<{
    month: string;
    count: number;
  }>;
}

// Create Movement Activity Interfaces
export interface CreateMovementActivityPayload {
  sourceSiteId?: string;
  sourceWarehouseId?: string;
  sourceVendorId?: string;
  destinationSiteId?: string;
  destinationWarehouseId?: string;
  destinationCustomerId?: string;
  activityType: string;
  activitySubType: string;
  /** Number of days hired; used when activityType is OUTFLOW and activitySubType is DELIVER. */
  daysHired?: number;
  items: Array<{
    particulars: string;
    inventoryId: string;
    goodQuantity: number;
    damagedQuantity: number;
    reason?: string;
  }>;
  plannedDate: string;
  notes: string;
  driverName?: string;
  driverPhoneNumber?: string;
  vehicleNumber?: string;
}

export interface SiteInventoryItem {
  inventory: Inventory;
  inventoryId: string;
  totalQuantity: number;
  goodQuantity: number;
  damagedQuantity: number;
}

// Movement Activities APIs
export const createMovementActivity = async (
  data: CreateMovementActivityPayload,
): Promise<MovementActivity> => {
  try {
    const response = await api.post<MovementActivity>("item-movements", data);
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to create movement activity");
    throw error;
  }
};

export const getSiteInventoryItems = async (
  siteId: string,
): Promise<SiteInventoryItem[]> => {
  try {
    const response = await api.get<SiteInventoryItem[]>(
      `item-movements/site/${siteId}/items`,
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to get site inventory items");
    throw error;
  }
};

export const getMovementActivities = async (
  page = 1,
  limit = 10,
  search = "",
  activitySubType?: string,
  warehouseId?: string,
  dateFrom?: string,
  dateTo?: string,
  status?: string,
): Promise<GetMovementActivitiesResponse> => {
  try {
    const params: {
      page: number;
      limit: number;
      search: string;
      activitySubType?: string;
      warehouseId?: string;
      dateFrom?: string;
      dateTo?: string;
      status?: string;
    } = {
      page,
      limit,
      search,
    };
    if (activitySubType) params.activitySubType = activitySubType;
    if (warehouseId) params.warehouseId = warehouseId;
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;
    if (status) params.status = status;
    const response = await api.get<GetMovementActivitiesResponse>(
      "item-movements",
      { params },
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to get movement activities");
    throw error;
  }
};

export const getMovementActivity = async (
  id: string,
): Promise<MovementActivity> => {
  try {
    const response = await api.get<MovementActivity>(`item-movements/${id}`);
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to get movement activity");
    throw error;
  }
};

export const getSiteMovements = async (
  siteId: string,
  page = 1,
  limit = 10,
  activitySubType?: string,
  status?: string,
  includeDetails = false,
): Promise<GetMovementActivitiesResponse> => {
  try {
    const params: {
      page: number;
      limit: number;
      activitySubType?: string;
      status?: string;
      includeDetails?: boolean;
    } = { page, limit };
    if (activitySubType) params.activitySubType = activitySubType;
    if (status) params.status = status;
    if (includeDetails) params.includeDetails = true;
    const response = await api.get<GetMovementActivitiesResponse>(
      `sites/${siteId}/movements`,
      { params },
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to get site movements");
    throw error;
  }
};

export interface UpdateMovementStatusPayload {
  status: string;
}

export interface UpdateMovementPayload {
  driverName?: string;
  driverPhoneNumber?: string;
  vehicleNumber?: string;
  returnedBy?: string;
  receivedBy?: string;
  approvedBy?: string;
  returnedDate?: string;
  projectType?: string | null;
}

export interface SetMovementInvoicesPayload {
  invoiceIds: string[];
}

export interface ExtendDaysHiredPayload {
  dateFrom: string;
  dateTo: string;
  notes?: string;
}

export const updateMovementStatus = async (
  movementId: string,
  data: UpdateMovementStatusPayload,
): Promise<MovementActivity> => {
  try {
    const response = await api.patch<MovementActivity>(
      `item-movements/${movementId}`,
      data,
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to update movement status");
    throw error;
  }
};

export const updateMovement = async (
  movementId: string,
  data: UpdateMovementPayload,
): Promise<MovementActivity> => {
  try {
    const response = await api.patch<MovementActivity>(
      `item-movements/${movementId}`,
      data,
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to update movement");
    throw error;
  }
};

/** Replace the full set of invoices linked to a movement (`[]` unlinks all). */
export const setMovementInvoices = async (
  movementId: string,
  invoiceIds: string[],
): Promise<MovementActivity> => {
  try {
    const response = await api.patch<MovementActivity>(
      `item-movements/${movementId}/invoices`,
      { invoiceIds },
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to update linked invoices");
    throw error;
  }
};

export const extendMovementDaysHired = async (
  movementId: string,
  data: ExtendDaysHiredPayload,
): Promise<MovementActivity> => {
  try {
    const response = await api.patch<MovementActivity>(
      `item-movements/${movementId}/extend-days-hired`,
      data,
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to extend days hired");
    throw error;
  }
};

export const cancelMovementActivity = async (
  movementId: string,
): Promise<MovementActivity> => {
  try {
    const response = await api.patch<MovementActivity>(
      `item-movements/${movementId}/cancel`,
      {},
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to cancel movement");
    throw error;
  }
};

export interface UploadedDocument {
  id: string;
  fileName: string;
  filePath: string;
  mimeType: string;
  movementId: string;
  uploadedAt: string;
}

export const uploadMovementDocument = async (
  movementId: string,
  file: File,
): Promise<UploadedDocument> => {
  try {
    const formData = new FormData();
    formData.append("file", file);

    const response = await api.post<UploadedDocument>(
      `item-movements/${movementId}/documents`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to upload document");
    throw error;
  }
};

export const getMovementActivityStatistics = async (
  warehouseId?: string,
  dateFrom?: string,
  dateTo?: string,
): Promise<MovementActivityStatistics> => {
  try {
    const params: { warehouseId?: string; dateFrom?: string; dateTo?: string } = {};
    if (warehouseId) params.warehouseId = warehouseId;
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;
    const response = await api.get<MovementActivityStatistics>(
      "item-movements/statistics",
      { params: Object.keys(params).length > 0 ? params : undefined },
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to get movement activity statistics");
    throw error;
  }
};

// PPE/Non Scaffolds APIs
export type PpeCondition = "NEW" | "GOOD" | "FAIR" | "WORN" | "DAMAGED";

export interface Ppe {
  id: string;
  name: string;
  size: string | null;
  unit: string | null;
  condition: PpeCondition;
  quantity: number;
  warehouseId: string;
  warehouse?: {
    id: string;
    name: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface PpeUser {
  id: string;
  fullName: string;
  jobTitle: string;
  location: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ProvidePpeItem {
  ppeId: string;
  quantity: number;
}

export interface ProvidePpeRequest {
  ppeUserId: string;
  providedDate?: string;
  items: ProvidePpeItem[];
}

export interface PurchasePpeItem {
  ppeId: string;
  quantity: number;
}

export interface PurchasePpeRequest {
  purchasedDate?: string;
  notes?: string;
  items: PurchasePpeItem[];
}

export interface PpePurchase {
  id: string;
  ppeId: string;
  quantity: number | string;
  purchasedDate: string;
  notes: string | null;
  purchasedByUserId: string;
  createdAt: string;
  ppe?: Ppe;
  purchasedByUser?: {
    id: string;
    fullName: string;
    email: string;
  } | null;
  documents?: PpeRecordDocument[];
}

export interface PpeRecordDocument {
  id: string;
  fileName: string;
  filePath: string;
  mimeType: string;
  uploadedAt: string;
  provisionId?: string;
  purchaseId?: string;
}

export type PpeRecordKind = "provisions" | "purchases";

export interface GetPpePurchasesResponse {
  data: PpePurchase[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface PurchasePpeResponse {
  purchases: Array<{
    id: string;
    ppeId: string;
    quantity: number | string;
    purchasedDate: string;
    notes: string | null;
    purchasedByUserId: string;
    createdAt: string;
  }>;
}

export interface CreatePpePayload {
  name: string;
  size?: string | null;
  unit?: string | null;
  condition: PpeCondition;
  quantity: number;
  warehouseId: string;
}

export interface UpdatePpePayload {
  name?: string;
  size?: string | null;
  unit?: string | null;
  condition?: PpeCondition;
  quantity?: number;
  warehouseId?: string;
}

export interface CreatePpeUserPayload {
  fullName: string;
  jobTitle: string;
  location?: string | null;
}

export interface UpdatePpeUserPayload {
  fullName?: string;
  jobTitle?: string;
  location?: string | null;
}

export interface GetPpeResponse {
  data: Ppe[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface GetPpeUsersResponse {
  data: PpeUser[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface PpeProvision {
  id: string;
  ppeUserId: string;
  ppeId: string;
  quantity: number;
  providedDate: string;
  providedByUserId: string;
  createdAt: string;
  ppe?: Ppe;
  ppeUser?: Pick<PpeUser, "id" | "fullName" | "jobTitle" | "location">;
  providedByUser?: {
    id: string;
    fullName: string;
    email: string;
  } | null;
  documents?: PpeRecordDocument[];
}

export interface GetPpeProvisionsResponse {
  data: PpeProvision[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface PpeStatistics {
  totalStockRecords: number;
  totalPpeUsers: number;
  totalProvisions: number;
  totalPurchases: number;
  totals: {
    stockQuantity: number;
    provisionedQuantity: number;
  };
  averages: {
    quantityPerStockRecord: number;
    quantityPerProvision: number;
  };
  stockStatus: {
    outOfStock: number;
    lowStock: number;
    inStock: number;
  };
  byCondition: Array<{
    condition: string;
    recordCount: number;
    totalQuantity: number;
  }>;
  byWarehouse: Array<{
    warehouseId: string;
    warehouseName: string;
    recordCount: number;
    totalQuantity: number;
  }>;
  byUnit: Array<{
    unit: string;
    recordCount: number;
    totalQuantity: number;
  }>;
  topStockItems: Array<{
    ppeId: string;
    name: string;
    quantity: number;
    unit?: string | null;
    warehouseName?: string;
  }>;
  topProvisionedItems: Array<{
    ppeId: string;
    name: string;
    totalQuantity: number;
    provisionCount: number;
    unit?: string | null;
  }>;
  topUsers: Array<{
    ppeUserId: string;
    fullName: string;
    jobTitle?: string | null;
    provisionCount: number;
    totalQuantity: number;
  }>;
  monthlyProvisionTrend: Array<{
    month: string;
    count: number;
    totalQuantity: number;
  }>;
}

export const createPpe = async (data: CreatePpePayload): Promise<Ppe> => {
  try {
    const response = await api.post<Ppe>("ppe", data);
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to create PPE/Non Scaffolds");
    throw error;
  }
};

export const getPpeList = async (
  page = 1,
  limit = 10,
  search = "",
  warehouseId?: string,
): Promise<GetPpeResponse> => {
  try {
    const params: {
      page: number;
      limit: number;
      search: string;
      warehouseId?: string;
    } = { page, limit, search };
    if (warehouseId) params.warehouseId = warehouseId;
    const response = await api.get<GetPpeResponse>("ppe", { params });
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to get PPE/Non Scaffolds list");
    throw error;
  }
};

export const getPpeById = async (id: string): Promise<Ppe> => {
  try {
    const response = await api.get<Ppe>(`ppe/${id}`);
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to get PPE/Non Scaffolds");
    throw error;
  }
};

export const updatePpe = async (
  id: string,
  data: UpdatePpePayload,
): Promise<Ppe> => {
  try {
    const response = await api.patch<Ppe>(`ppe/${id}`, data);
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to update PPE/Non Scaffolds");
    throw error;
  }
};

export const createPpeUser = async (
  data: CreatePpeUserPayload,
): Promise<PpeUser> => {
  try {
    const response = await api.post<PpeUser>("ppe/users", data);
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to create PPE/Non Scaffolds user");
    throw error;
  }
};

export const getPpeUsers = async (
  page = 1,
  limit = 10,
  search = "",
): Promise<GetPpeUsersResponse> => {
  try {
    const response = await api.get<GetPpeUsersResponse>("ppe/users", {
      params: { page, limit, search },
    });
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to get PPE/Non Scaffolds users");
    throw error;
  }
};

export const updatePpeUser = async (
  id: string,
  data: UpdatePpeUserPayload,
): Promise<PpeUser> => {
  try {
    const response = await api.patch<PpeUser>(`ppe/users/${id}`, data);
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to update PPE/Non Scaffolds user");
    throw error;
  }
};

export const getPpeUserProvisions = async (
  userId: string,
  page = 1,
  limit = 10,
  search = "",
): Promise<GetPpeProvisionsResponse> => {
  try {
    const response = await api.get<GetPpeProvisionsResponse>(
      `ppe/users/${userId}/provisions`,
      { params: { page, limit, search } },
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to get PPE/Non Scaffolds provisions for user");
    throw error;
  }
};

export const getPpeProvisions = async (
  page = 1,
  limit = 10,
  search = "",
  ppeUserId?: string,
  ppeId?: string,
): Promise<GetPpeProvisionsResponse> => {
  try {
    const params: {
      page: number;
      limit: number;
      search: string;
      ppeUserId?: string;
      ppeId?: string;
    } = { page, limit, search };
    if (ppeUserId) params.ppeUserId = ppeUserId;
    if (ppeId) params.ppeId = ppeId;
    const response = await api.get<GetPpeProvisionsResponse>("ppe/provisions", {
      params,
    });
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to get PPE/Non Scaffolds provisions");
    throw error;
  }
};

export const providePpe = async (
  data: ProvidePpeRequest,
): Promise<{
  ppeUserId: string;
  provisions: Array<{
    id: string;
    ppeUserId: string;
    ppeId: string;
    quantity: number;
    providedDate: string;
    providedByUserId: string;
    createdAt: string;
  }>;
}> => {
  try {
    const response = await api.post("ppe/provisions", data);
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to provide PPE/Non Scaffolds");
    throw error;
  }
};

export const getPpePurchases = async (
  page = 1,
  limit = 10,
  search = "",
  ppeId?: string,
): Promise<GetPpePurchasesResponse> => {
  try {
    const params: {
      page: number;
      limit: number;
      search: string;
      ppeId?: string;
    } = { page, limit, search };
    if (ppeId) params.ppeId = ppeId;
    const response = await api.get<GetPpePurchasesResponse>("ppe/purchases", {
      params,
    });
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to get PPE/Non Scaffolds purchases");
    throw error;
  }
};

export const getPpeRecord = async (
  kind: PpeRecordKind,
  id: string,
): Promise<PpeProvision | PpePurchase> => {
  try {
    const response = await api.get<PpeProvision | PpePurchase>(`ppe/${kind}/${id}`);
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to get PPE/Non Scaffolds record");
    throw error;
  }
};

export const uploadPpeRecordDocument = async (
  kind: PpeRecordKind,
  id: string,
  file: File,
): Promise<PpeRecordDocument> => {
  try {
    const formData = new FormData();
    formData.append("file", file);
    const response = await api.post<PpeRecordDocument>(
      `ppe/${kind}/${id}/documents`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to upload document");
    throw error;
  }
};

export const deletePpeRecordDocument = async (
  kind: PpeRecordKind,
  id: string,
  documentId: string,
): Promise<{ deleted: boolean; documentId: string }> => {
  try {
    const response = await api.delete<{ deleted: boolean; documentId: string }>(
      `ppe/${kind}/${id}/documents/${documentId}`,
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to delete document");
    throw error;
  }
};

export const purchasePpe = async (
  data: PurchasePpeRequest,
): Promise<PurchasePpeResponse> => {
  try {
    const response = await api.post<PurchasePpeResponse>("ppe/purchases", data);
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to record PPE/Non Scaffolds purchase");
    throw error;
  }
};

export const getPpeStatistics = async (
  warehouseId?: string,
  dateFrom?: string,
  dateTo?: string,
): Promise<PpeStatistics> => {
  try {
    const params: {
      warehouseId?: string;
      dateFrom?: string;
      dateTo?: string;
    } = {};
    if (warehouseId) params.warehouseId = warehouseId;
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;
    const response = await api.get<PpeStatistics>("ppe/statistics", { params });
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to get PPE/Non Scaffolds statistics");
    throw error;
  }
};

// Quotation Interfaces
/** Column config sent with create/update (columns + visibility). */
export interface DocumentColumnConfig {
  columns: ColumnDef[];
  columnVisibility?: Record<string, boolean>;
}

export interface BankDetail {
  id: string;
  bankName: string;
  accountName: string;
  accountNumber: string;
  swiftCode: string | null;
  branchName: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateBankDetailPayload {
  bankName: string;
  accountName: string;
  accountNumber: string;
  swiftCode?: string;
  branchName?: string;
}

export type UpdateBankDetailPayload = Partial<CreateBankDetailPayload>;

export interface QuotationItem {
  id: string;
  description: string;
  quantity: number;
  unit: string;
  unitPrice: string;
  amount: string;
  startDate: string;
  endDate: string;
  quotationId: string;
  inventoryId?: string;
  /** Custom column values from additional columns. */
  data?: Record<string, string | number>;
}

export interface Quotation {
  id: string;
  reference: string;
  issueDate: string;
  expiryDate: string;
  currencyType: string;
  amount: string;
  vat: string;
  totalAmount: string;
  isVatIncluded: boolean;
  showIsVatExempted?: boolean;
  status: string;
  customerId: string;
  siteId: string;
  title?: string;
  notes?: string;
  /** Introductory text above the items table (e.g. "We are pleased to quote you for the following"). */
  description?: string;
  bankDetailId?: string;
  bankDetail?: BankDetail;
  createdAt: string;
  updatedAt: string;
  customer: Customer;
  /** Omitted when quotation is created without a site. */
  site?: Site;
  /** Present on detail (findOne/create/update); omitted from light list responses. */
  items?: QuotationItem[];
  /** Column definitions and visibility returned by GET. */
  columnConfig?: DocumentColumnConfig;
  pdfUrl?: string;
}

export interface GetQuotationsResponse {
  data: Quotation[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface QuotationStatistics {
  totalQuotations: number;
  totals: {
    items: number;
    amount: number;
    vat: number;
    totalAmount: number;
  };
  averages: {
    itemsPerQuotation: number;
    amountPerQuotation: number;
    totalAmountPerQuotation: number;
  };
  byStatus: Array<{
    status: string;
    count: number;
    totals: {
      amount: number;
      vat: number;
      totalAmount: number;
    };
  }>;
  byCurrency: Array<{
    currencyType: string;
    count: number;
    totalAmount: number;
  }>;
  byVatInclusion: {
    vatIncluded: number;
    vatExcluded: number;
  };
  monthlyTrend: Array<{
    month: string;
    count: number;
    totalAmount: number;
  }>;
  topCustomers: Array<{
    customerId: string;
    customerName: string;
    quotationCount: number;
    totalAmount: number;
  }>;
}

// Quotation APIs
// Quotation APIs
export interface CreateQuotationItem {
  description: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  amount: number;
  startDate: string;
  endDate: string;
  inventoryId?: string;
  /** Custom column values: key -> string | number. */
  data?: Record<string, string | number>;
}

export interface CreateQuotationPayload {
  customerId: string;
  /** Optional; omit when creating a quotation without a site. */
  siteId?: string;
  issueDate: string;
  /** Expiry date (yyyy-MM-dd). Send null on update to clear. */
  expiryDate?: string | null;
  currencyType: string;
  amount: number;
  isVatIncluded: boolean;
  showIsVatExempted?: boolean;
  title?: string;
  notes?: string;
  reference?: string;
  /** Introductory text above the items table (e.g. "We are pleased to quote you for the following"). */
  description?: string;
  /** Omit or send null to clear linked bank details on update. */
  bankDetailId?: string | null;
  items: CreateQuotationItem[];
  /** Column definitions and visibility for the item table. */
  columnConfig?: DocumentColumnConfig;
}

export const createQuotation = async (
  data: CreateQuotationPayload,
): Promise<Quotation> => {
  try {
    const response = await api.post<Quotation>("quotations", data);
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to create quotation");
    throw error;
  }
};

export const updateQuotation = async (
  id: string,
  data: Partial<CreateQuotationPayload>,
): Promise<Quotation> => {
  try {
    const response = await api.patch<Quotation>(`quotations/${id}`, data);
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to update quotation");
    throw error;
  }
};

export const updateQuotationStatus = async (
  id: string,
  status: string,
): Promise<Quotation> => {
  try {
    const response = await api.patch<Quotation>(`quotations/${id}`, { status });
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to update quotation status");
    throw error;
  }
};

export const getQuotations = async (
  page = 1,
  limit = 10,
  search = "",
  status?: string,
): Promise<GetQuotationsResponse> => {
  try {
    const params: { page: number; limit: number; search: string; status?: string } = {
      page,
      limit,
      search,
    };
    if (status) params.status = status;
    const response = await api.get<GetQuotationsResponse>("quotations", {
      params,
    });
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to get quotations");
    throw error;
  }
};

export const getQuotation = async (id: string): Promise<Quotation> => {
  try {
    const response = await api.get<Quotation>(`quotations/${id}`);
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to get quotation details");
    throw error;
  }
};

export const getQuotationStatistics =
  async (): Promise<QuotationStatistics> => {
    try {
      const response = await api.get<QuotationStatistics>(
        "quotations/statistics",
      );
      return response.data;
    } catch (error) {
      handleAxiosError(error, "Failed to get quotation statistics");
      throw error;
    }
  };

// Bank Details
export interface GetBankDetailsResponse {
  data: BankDetail[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export const getBankDetails = async (
  page = 1,
  limit = 10,
  search = "",
): Promise<GetBankDetailsResponse> => {
  try {
    const params: { page: number; limit: number; search?: string } = { page, limit };
    if (search) params.search = search;
    const response = await api.get<GetBankDetailsResponse>("bank-details", {
      params,
    });
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to get bank details");
    throw error;
  }
};

export const getBankDetail = async (id: string): Promise<BankDetail> => {
  try {
    const response = await api.get<BankDetail>(`bank-details/${id}`);
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to get bank detail");
    throw error;
  }
};

export const createBankDetail = async (
  data: CreateBankDetailPayload,
): Promise<BankDetail> => {
  try {
    const response = await api.post<BankDetail>("bank-details", data);
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to create bank detail");
    throw error;
  }
};

export const updateBankDetail = async (
  id: string,
  data: UpdateBankDetailPayload,
): Promise<BankDetail> => {
  try {
    const response = await api.patch<BankDetail>(`bank-details/${id}`, data);
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to update bank detail");
    throw error;
  }
};

// Invoice Interfaces
export interface InvoiceItem {
  id: string;
  description: string;
  quantity: number;
  unit: string;
  unitPrice: string;
  amount: string;
  startDate: string;
  endDate: string;
  invoiceId: string;
  /** Custom column values from additional columns. */
  data?: Record<string, string | number>;
}

export interface ItemMovementSummary {
  id: string;
  reference: string;
  status: string;
  activityType: string;
  activitySubType: string;
  createdAt: string;
}

export interface Invoice {
  id: string;
  reference: string;
  issueDate: string;
  dueDate: string;
  currencyType: string;
  amount: string;
  vat: string;
  totalAmount: string;
  isVatIncluded: boolean;
  showIsVatExempted?: boolean;
  status: string;
  customerId: string;
  siteId: string;
  title?: string;
  notes?: string;
  /** Introductory text above the items table (e.g. "We are pleased to quote you for the following"). */
  description?: string;
  bankDetailId?: string;
  bankDetail?: BankDetail;
  createdAt: string;
  updatedAt: string;
  customer: Customer;
  /** Omitted when invoice is created without a site. */
  site?: Site;
  /** Present on detail (findOne/create/update); omitted from light list responses. */
  items?: InvoiceItem[];
  /** Item movements linked to this invoice. */
  movements?: ItemMovementSummary[];
  /** Column definitions and visibility returned by GET. */
  columnConfig?: DocumentColumnConfig;
  pdfUrl?: string;
}

export interface GetInvoicesResponse {
  data: Invoice[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface InvoiceStatistics {
  totalInvoices: number;
  totals: {
    items: number;
    amount: number;
    vat: number;
    totalAmount: number;
    paid: number;
    unpaid: number;
  };
  averages: {
    itemsPerInvoice: number;
    amountPerInvoice: number;
    totalAmountPerInvoice: number;
  };
  byStatus: Array<{
    status: string;
    count: number;
    totals: {
      amount: number;
      vat: number;
      totalAmount: number;
    };
  }>;
  byCurrency: Array<{
    currencyType: string;
    count: number;
    totalAmount: number;
  }>;
  byVatInclusion: {
    vatIncluded: number;
    vatExcluded: number;
  };
  monthlyTrend: Array<{
    month: string;
    count: number;
    totalAmount: number;
  }>;
  topCustomers: Array<{
    customerId: string;
    customerName: string;
    invoiceCount: number;
    totalAmount: number;
  }>;
}

// Invoice APIs
export const getInvoices = async (
  page = 1,
  limit = 10,
  search = "",
  status?: string,
): Promise<GetInvoicesResponse> => {
  try {
    const params: { page: number; limit: number; search: string; status?: string } = {
      page,
      limit,
      search,
    };
    if (status) params.status = status;
    const response = await api.get<GetInvoicesResponse>("invoices", {
      params,
    });
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to get invoices");
    throw error;
  }
};

export const getInvoiceStatistics = async (): Promise<InvoiceStatistics> => {
  try {
    const response = await api.get<InvoiceStatistics>("invoices/statistics");
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to get invoice statistics");
    throw error;
  }
};

export const getInvoice = async (id: string): Promise<Invoice> => {
  try {
    const response = await api.get<Invoice>(`invoices/${id}`);
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to get invoice details");
    throw error;
  }
};

// Invoice Create/Update payloads
export interface CreateInvoiceItem {
  description: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  amount: number;
  startDate: string;
  endDate: string;
  /** Custom column values: key -> string | number. */
  data?: Record<string, string | number>;
}

export interface CreateInvoicePayload {
  customerId: string;
  /** Optional; omit when creating an invoice without a site. */
  siteId?: string;
  issueDate: string;
  /** Due date (yyyy-MM-dd). Send null on update to clear. */
  dueDate?: string | null;
  currencyType: string;
  amount: number;
  isVatIncluded: boolean;
  showIsVatExempted?: boolean;
  title?: string;
  notes?: string;
  reference?: string;
  /** Introductory text above the items table (e.g. "We are pleased to quote you for the following"). */
  description?: string;
  /** Omit or send null to clear linked bank details on update. */
  bankDetailId?: string | null;
  items: CreateInvoiceItem[];
  status?: string;
  /** Column definitions and visibility for the item table. */
  columnConfig?: DocumentColumnConfig;
}

export const createInvoice = async (
  data: CreateInvoicePayload,
): Promise<Invoice> => {
  try {
    const response = await api.post<Invoice>("invoices", data);
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to create invoice");
    throw error;
  }
};

export const updateInvoice = async (
  id: string,
  data: Partial<CreateInvoicePayload>,
): Promise<Invoice> => {
  try {
    const response = await api.patch<Invoice>(`invoices/${id}`, data);
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to update invoice");
    throw error;
  }
};

// Report Interfaces
export interface SalesReport {
  period: {
    start: string;
    end: string;
  };
  quotations: {
    total: number;
    items: number;
    totals: {
      amount: number;
      vat: number;
      totalAmount: number;
    };
  };
  invoices: {
    total: number;
    items: number;
    totals: {
      amount: number;
      vat: number;
      totalAmount: number;
      paid: number;
      unpaid: number;
    };
  };
  byCurrency: {
    [key: string]: {
      quotations: {
        count: number;
        totalAmount: number;
      };
      invoices: {
        count: number;
        totalAmount: number;
        paid: number;
        unpaid: number;
      };
    };
  };
}

export interface InventoryReport {
  totals: {
    items: number;
    available: number;
    damaged: number;
    rented: number;
    reserved: number;
    lost: number;
    sold: number;
    total: number;
    value: number;
  };
  byWarehouse: Array<{
    warehouseId: string;
    warehouseName: string;
    items: number;
    available: number;
    damaged: number;
    rented: number;
    reserved: number;
    lost: number;
    sold: number;
    total: number;
    value: number;
  }>;
}

export interface MovementsReport {
  period: {
    start: string;
    end: string;
  };
  totals: {
    movements: number;
    closedMovements: number;
    items: number;
    quantity: number;
  };
  byActivityType: Array<{
    activityType: string;
    count: number;
    items: number;
    quantity: number;
  }>;
  byActivitySubType: Array<{
    activitySubType: string;
    count: number;
    items: number;
    quantity: number;
  }>;
  byStatus: Array<{
    status: string;
    count: number;
    items: number;
    quantity: number;
  }>;
}

export interface FinancialReport {
  period: {
    start: string;
    end: string;
  };
  totals: {
    quotations: {
      count: number;
      totalAmount: number;
    };
    invoices: {
      count: number;
      totalAmount: number;
      paid: number;
      unpaid: number;
    };
    netRevenue: number;
  };
  byCustomer: Array<{
    customerId: string;
    customerName: string;
    quotations: number;
    invoices: number;
    quotationAmount: number;
    invoiceAmount: number;
    paidAmount: number;
    unpaidAmount: number;
  }>;
}

export interface CustomerReportData {
  customerId: string;
  customerName: string;
  type: string;
  sites: number;
  quotations: {
    count: number;
    totalAmount: number;
  };
  invoices: {
    count: number;
    totalAmount: number;
    paid: number;
    unpaid: number;
  };
}

export interface CustomersReport {
  period: {
    start: string;
    end: string;
  };
  customers: CustomerReportData[];
  totals: {
    customers: number;
    sites: number;
    quotations: number;
    invoices: number;
  };
}

// Report Query Types
export enum ReportPeriod {
  DAILY = "DAILY",
  WEEKLY = "WEEKLY",
  MONTHLY = "MONTHLY",
  YEARLY = "YEARLY",
  CUSTOM = "CUSTOM",
}

export interface ReportQueryDto {
  startDate?: string;
  endDate?: string;
  period?: ReportPeriod;
  customerId?: string;
  siteId?: string;
  warehouseId?: string;
}

// Report APIs
export const getSalesReport = async (
  query?: ReportQueryDto,
): Promise<SalesReport> => {
  try {
    const response = await api.get<SalesReport>("reports/sales", {
      params: query,
    });
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to get sales report");
    throw error;
  }
};

export const getInventoryReport = async (
  query?: ReportQueryDto,
): Promise<InventoryReport> => {
  try {
    const response = await api.get<InventoryReport>("reports/inventory", {
      params: query,
    });
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to get inventory report");
    throw error;
  }
};

export const getMovementsReport = async (
  query?: ReportQueryDto,
): Promise<MovementsReport> => {
  try {
    const response = await api.get<MovementsReport>("reports/movements", {
      params: query,
    });
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to get movements report");
    throw error;
  }
};

export const getFinancialReport = async (
  query?: ReportQueryDto,
): Promise<FinancialReport> => {
  try {
    const response = await api.get<FinancialReport>("reports/financial", {
      params: query,
    });
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to get financial report");
    throw error;
  }
};

export const getCustomersReport = async (
  query?: ReportQueryDto,
): Promise<CustomersReport> => {
  try {
    const response = await api.get<CustomersReport>("reports/customers", {
      params: query,
    });
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to get customers report");
    throw error;
  }
};

// Dashboard Interfaces
export interface DashboardOverview {
  summary: {
    customers: number;
    sites: number;
    warehouses: number;
    inventoryItems: number;
    quotations: number;
    invoices: number;
    movements: number;
    users: number;
  };
  thisMonth: {
    quotations: {
      count: number;
      totalAmount: number;
    };
    invoices: {
      count: number;
      totalAmount: number;
      paid: number;
    };
  };
  pending: {
    quotations: number;
    invoices: number;
    overdueInvoices: number;
    movements: number;
  };
  inventory: {
    totalAvailable: number;
    totalRented: number;
    totalReserved: number;
    totalDamaged: number;
    totalLost: number;
    totalSold: number;
    totalValue: number;
  };
}

export interface DashboardActivity {
  type: "movement" | "invoice" | "quotation";
  id: string;
  activityType?: string;
  activitySubType?: string;
  status: string;
  source?: string;
  destination?: string;
  reference?: string;
  currencyType?: string;
  amount?: string;
  customer?: string;
  site?: string;
  createdAt: string;
}

export interface DashboardRecentActivity {
  activities: DashboardActivity[];
  total: number;
}

export interface DashboardQuickMetrics {
  revenue: {
    thisMonth: number;
    lastMonth: number;
    growth: number;
  };
  outstanding: {
    count: number;
    amount: number;
  };
  inventory: {
    activeRentals: number;
    lowStockItems: number;
  };
  customers: {
    thisMonth: number;
  };
  conversion: {
    rate: number;
    quotations: number;
    invoices: number;
  };
}

export interface MonthlyTrendData {
  month: string;
  quotations: {
    count: number;
    total: number;
  };
  invoices: {
    count: number;
    total: number;
    paid: number;
  };
  movements: number;
  customers: number;
}

export interface DashboardMonthlyTrend {
  trends: MonthlyTrendData[];
  period: number;
}

// Dashboard APIs
export const getDashboardOverview = async (): Promise<DashboardOverview> => {
  try {
    const response = await api.get<DashboardOverview>("dashboard/overview");
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to get dashboard overview");
    throw error;
  }
};

export const getDashboardRecentActivity =
  async (): Promise<DashboardRecentActivity> => {
    try {
      const response = await api.get<DashboardRecentActivity>(
        "dashboard/recent-activity",
      );
      return response.data;
    } catch (error) {
      handleAxiosError(error, "Failed to get recent activity");
      throw error;
    }
  };

export const getDashboardQuickMetrics =
  async (): Promise<DashboardQuickMetrics> => {
    try {
      const response = await api.get<DashboardQuickMetrics>(
        "dashboard/quick-metrics",
      );
      return response.data;
    } catch (error) {
      handleAxiosError(error, "Failed to get quick metrics");
      throw error;
    }
  };

export const getDashboardMonthlyTrend =
  async (): Promise<DashboardMonthlyTrend> => {
    try {
      const response = await api.get<DashboardMonthlyTrend>(
        "dashboard/monthly-trend",
      );
      return response.data;
    } catch (error) {
      handleAxiosError(error, "Failed to get monthly trend");
      throw error;
    }
  };

// Fixed assets (depreciation)
export interface FixedAssetAddition {
  id: string;
  fixedAssetId: string;
  date: string;
  amount: string;
  notes: string | null;
  createdAt: string;
}

export interface FixedAsset {
  id: string;
  division: string | null;
  description: string;
  units: number | null;
  serialNumber: string | null;
  depreciationRate: string | null;
  datePurchased: string | null;
  costPrice: string;
  openingAccumulatedDepreciation: string;
  disposedAt: string | null;
  disposalAmount: string | null;
  additions?: FixedAssetAddition[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateFixedAssetPayload {
  division?: string | null;
  description: string;
  units?: number | null;
  serialNumber?: string | null;
  depreciationRate?: string | null;
  datePurchased?: string | null;
  costPrice?: string;
  openingAccumulatedDepreciation?: string;
  disposedAt?: string | null;
  disposalAmount?: string | null;
}

export type UpdateFixedAssetPayload = Partial<CreateFixedAssetPayload>;

export interface AddFixedAssetAdditionPayload {
  date: string;
  amount: string;
  notes?: string | null;
}

export interface FixedAssetYearSummary {
  year: number;
  additions: number;
  depreciation: number;
  basisEnd: number;
  accumulatedDepreciationEnd: number;
  nbvEnd: number;
}

export interface FixedAssetDepreciationReport {
  fixedAssetId: string;
  fromYear: number;
  toYear: number;
  rate: number;
  openingAccumulatedDepreciation: number;
  years: FixedAssetYearSummary[];
  /** Present when depreciation cannot be computed (e.g. missing datePurchased). */
  message?: string;
}

export interface FixedAssetDivisionSummary {
  division: string;
  count: number;
  costBasis: number;
  accumulatedDepreciation: number;
  nbv: number;
}

export interface FixedAssetStatisticsResponse {
  totals: {
    totalAssets: number;
    activeAssets: number;
    disposedAssets: number;
    costBasis: number;
    accumulatedDepreciation: number;
    nbv: number;
  };
  byDivision: FixedAssetDivisionSummary[];
}

export interface GetFixedAssetsResponse {
  data: FixedAsset[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export const createFixedAsset = async (
  data: CreateFixedAssetPayload,
): Promise<FixedAsset> => {
  try {
    const response = await api.post<FixedAsset>("assets", data);
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to create fixed asset");
    throw error;
  }
};

export const getFixedAssets = async (
  page = 1,
  limit = 10,
  search = "",
): Promise<GetFixedAssetsResponse> => {
  try {
    const response = await api.get<GetFixedAssetsResponse>("assets", {
      params: { page, limit, search },
    });
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to get fixed assets");
    throw error;
  }
};

export const getFixedAssetStatistics =
  async (): Promise<FixedAssetStatisticsResponse> => {
    try {
      const response =
        await api.get<FixedAssetStatisticsResponse>("assets/statistics");
      return response.data;
    } catch (error) {
      handleAxiosError(error, "Failed to get asset statistics");
      throw error;
    }
  };

export interface AllAssetsDepreciationItem {
  asset: FixedAsset;
  fromYear?: number;
  toYear?: number;
  rate?: number;
  openingAccumulatedDepreciation?: number;
  years: FixedAssetYearSummary[];
  message?: string;
}

export interface GetAllAssetsDepreciationResponse {
  data: AllAssetsDepreciationItem[];
  meta: {
    total: number;
    fromYear: number | null;
    toYear: number | null;
    division: string | null;
    search: string | null;
  };
}

export const getAllAssetsDepreciation = async ({
  fromYear,
  toYear,
  division,
  search,
}: {
  fromYear?: number;
  toYear?: number;
  division?: string;
  search?: string;
} = {}): Promise<GetAllAssetsDepreciationResponse> => {
  try {
    const params: Record<string, string | number> = {};
    if (fromYear != null) params.fromYear = fromYear;
    if (toYear != null) params.toYear = toYear;
    if (division != null && String(division).trim()) params.division = String(division).trim();
    if (search != null && String(search).trim()) params.search = String(search).trim();
    const response = await api.get<GetAllAssetsDepreciationResponse>(
      "assets/depreciation",
      { params },
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to get all-assets depreciation report");
    throw error;
  }
};

export const getFixedAssetById = async (id: string): Promise<FixedAsset> => {
  try {
    const response = await api.get<FixedAsset>(`assets/${id}`);
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to get fixed asset");
    throw error;
  }
};

export const updateFixedAsset = async (
  id: string,
  data: UpdateFixedAssetPayload,
): Promise<FixedAsset> => {
  try {
    const response = await api.patch<FixedAsset>(`assets/${id}`, data);
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to update fixed asset");
    throw error;
  }
};

export const addFixedAssetAddition = async (
  assetId: string,
  data: AddFixedAssetAdditionPayload,
): Promise<FixedAssetAddition> => {
  try {
    const response = await api.post<FixedAssetAddition>(
      `assets/${assetId}/additions`,
      data,
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to add asset addition");
    throw error;
  }
};

export const getFixedAssetDepreciation = async (
  id: string,
  fromYear?: number,
  toYear?: number,
): Promise<FixedAssetDepreciationReport> => {
  try {
    const params: Record<string, number> = {};
    if (fromYear !== undefined) params.fromYear = fromYear;
    if (toYear !== undefined) params.toYear = toYear;
    const response = await api.get<FixedAssetDepreciationReport>(
      `assets/${id}/depreciation`,
      { params },
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to get depreciation report");
    throw error;
  }
};

// Shop APIs
export type ShopRecordStatus = "ACTIVE" | "DISABLED";
export type ProductOrderStatus = "PLACED" | "CANCELLED";
export type ProductOrderLineType = "RENT" | "PURCHASE";

export interface ProductCategory {
  id: string;
  name: string;
  status: ShopRecordStatus;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface GetProductCategoriesResponse {
  data: ProductCategory[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface CreateProductCategoryPayload {
  name: string;
  status?: ShopRecordStatus;
  sortOrder?: number;
}

export interface UpdateProductCategoryPayload {
  name?: string;
  status?: ShopRecordStatus;
  sortOrder?: number | null;
}

export const getProductCategories = async (
  page = 1,
  limit = 10,
  search = "",
  status?: ShopRecordStatus,
): Promise<GetProductCategoriesResponse> => {
  try {
    const params: {
      page: number;
      limit: number;
      search: string;
      status?: ShopRecordStatus;
    } = { page, limit, search };
    if (status) params.status = status;
    const response = await api.get<GetProductCategoriesResponse>(
      "product-categories",
      { params },
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to get product categories");
    throw error;
  }
};

export const getProductCategory = async (
  id: string,
): Promise<ProductCategory> => {
  try {
    const response = await api.get<ProductCategory>(
      `product-categories/${id}`,
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to get product category");
    throw error;
  }
};

export const createProductCategory = async (
  data: CreateProductCategoryPayload,
): Promise<ProductCategory> => {
  try {
    const response = await api.post<ProductCategory>(
      "product-categories",
      data,
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to create product category");
    throw error;
  }
};

export const updateProductCategory = async (
  id: string,
  data: UpdateProductCategoryPayload,
): Promise<ProductCategory> => {
  try {
    const response = await api.patch<ProductCategory>(
      `product-categories/${id}`,
      data,
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to update product category");
    throw error;
  }
};

export const deleteProductCategory = async (id: string): Promise<void> => {
  try {
    await api.delete(`product-categories/${id}`);
  } catch (error) {
    handleAxiosError(error, "Failed to delete product category");
    throw error;
  }
};

export interface Product {
  id: string;
  categoryId: string;
  name: string;
  summary: string | null;
  detail: string | null;
  images: string[];
  videos: string[];
  purchasePrice: number;
  offerPrice: number;
  status: ShopRecordStatus;
  createdAt: string;
  updatedAt: string;
  category?: ProductCategory;
}

export interface GetProductsResponse {
  data: Product[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface CreateProductPayload {
  categoryId: string;
  name: string;
  summary?: string;
  detail?: string;
  images?: string[];
  videos?: string[];
  purchasePrice: number;
  offerPrice: number;
  status?: ShopRecordStatus;
}

export interface UpdateProductPayload {
  categoryId?: string;
  name?: string;
  summary?: string;
  detail?: string;
  images?: string[];
  videos?: string[];
  purchasePrice?: number;
  offerPrice?: number;
  status?: ShopRecordStatus;
}

export const getProducts = async (
  page = 1,
  limit = 10,
  search = "",
  categoryId?: string,
  status?: ShopRecordStatus,
): Promise<GetProductsResponse> => {
  try {
    const params: {
      page: number;
      limit: number;
      search: string;
      categoryId?: string;
      status?: ShopRecordStatus;
    } = { page, limit, search };
    if (categoryId) params.categoryId = categoryId;
    if (status) params.status = status;
    const response = await api.get<GetProductsResponse>("products", { params });
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to get products");
    throw error;
  }
};

export const getProduct = async (id: string): Promise<Product> => {
  try {
    const response = await api.get<Product>(`products/${id}`);
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to get product");
    throw error;
  }
};

export const createProduct = async (
  data: CreateProductPayload,
): Promise<Product> => {
  try {
    const response = await api.post<Product>("products", data);
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to create product");
    throw error;
  }
};

export const updateProduct = async (
  id: string,
  data: UpdateProductPayload,
): Promise<Product> => {
  try {
    const response = await api.patch<Product>(`products/${id}`, data);
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to update product");
    throw error;
  }
};

export const deleteProduct = async (id: string): Promise<void> => {
  try {
    await api.delete(`products/${id}`);
  } catch (error) {
    handleAxiosError(error, "Failed to delete product");
    throw error;
  }
};

export const uploadProductImage = async (
  productId: string,
  file: File,
): Promise<Product> => {
  try {
    const formData = new FormData();
    formData.append("file", file);
    const response = await api.post<Product>(
      `products/${productId}/images`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to upload product image");
    throw error;
  }
};

export const removeProductImage = async (
  productId: string,
  image: string,
): Promise<Product> => {
  try {
    const response = await api.delete<Product>(`products/${productId}/images`, {
      data: { image },
    });
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to remove product image");
    throw error;
  }
};

export const uploadProductVideo = async (
  productId: string,
  file: File,
): Promise<Product> => {
  try {
    const formData = new FormData();
    formData.append("file", file);
    const response = await api.post<Product>(
      `products/${productId}/videos`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to upload product video");
    throw error;
  }
};

export const removeProductVideo = async (
  productId: string,
  video: string,
): Promise<Product> => {
  try {
    const response = await api.delete<Product>(`products/${productId}/videos`, {
      data: { video },
    });
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to remove product video");
    throw error;
  }
};

export interface ProductOrderUser {
  id: string;
  fullName?: string;
  email?: string;
  phoneNumber?: string;
}

export interface ProductOrderItem {
  id: string;
  productId: string;
  productName?: string;
  orderType: ProductOrderLineType;
  quantity: number;
  /** Hire duration for RENT lines; null/omit for PURCHASE. */
  daysHired?: number | null;
  days_hired?: number | null;
  unitPrice?: number;
  lineTotal?: number;
  unit_price?: number;
  line_total?: number;
  product?: Product;
}

export interface LinkedOrderInvoice {
  id: string;
  reference: string;
  pdfUrl?: string | null;
}

export interface LinkedOrderQuotation {
  id: string;
  reference: string;
  pdfUrl?: string | null;
}

export interface EntityAttachment {
  id: string;
  typeName: string;
  fileUrl: string;
  originalFileName: string;
  mimeType: string;
  fileSize: number;
  createdAt: string;
}

export type ExternalDocumentKind = "INVOICE" | "QUOTATION";

export interface ExternalDocument {
  id: string;
  kind: ExternalDocumentKind;
  fileUrl: string;
  originalFileName: string;
  mimeType: string;
  fileSize: number;
  createdAt: string;
}

export interface ProductOrder {
  id: string;
  reference: string;
  status: ProductOrderStatus;
  location?: string | null;
  userId: string;
  user?: ProductOrderUser;
  items: ProductOrderItem[];
  invoices: LinkedOrderInvoice[];
  quotations?: LinkedOrderQuotation[];
  quotation?: LinkedOrderQuotation | null;
  attachments?: EntityAttachment[];
  documents?: ExternalDocument[];
  uploadedInvoices?: ExternalDocument[];
  uploadedQuotations?: ExternalDocument[];
  createdAt: string;
  updatedAt: string;
}

export interface GetProductOrdersResponse {
  data: ProductOrder[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export const getProductOrders = async (
  page = 1,
  limit = 10,
  search = "",
  filters?: {
    userId?: string;
    reference?: string;
    status?: ProductOrderStatus;
    invoiceId?: string;
    quotationId?: string;
  },
): Promise<GetProductOrdersResponse> => {
  try {
    const params: Record<string, string | number> = { page, limit, search };
    if (filters?.userId) params.userId = filters.userId;
    if (filters?.reference) params.reference = filters.reference;
    if (filters?.status) params.status = filters.status;
    if (filters?.invoiceId) params.invoiceId = filters.invoiceId;
    if (filters?.quotationId) params.quotationId = filters.quotationId;
    const response = await api.get<GetProductOrdersResponse>("product-orders", {
      params,
    });
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to get product orders");
    throw error;
  }
};

export const getProductOrder = async (id: string): Promise<ProductOrder> => {
  try {
    const response = await api.get<ProductOrder>(`product-orders/${id}`);
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to get product order");
    throw error;
  }
};

export const setProductOrderInvoices = async (
  orderId: string,
  invoiceIds: string[],
): Promise<ProductOrder> => {
  try {
    const response = await api.patch<ProductOrder>(
      `product-orders/${orderId}/invoices`,
      { invoiceIds },
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to update linked invoices");
    throw error;
  }
};

export const setProductOrderQuotations = async (
  orderId: string,
  quotationIds: string[],
): Promise<ProductOrder> => {
  try {
    const response = await api.patch<ProductOrder>(
      `product-orders/${orderId}/quotations`,
      { quotationIds },
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to update linked quotations");
    throw error;
  }
};

export const uploadProductOrderDocument = async (
  orderId: string,
  kind: ExternalDocumentKind,
  file: File,
): Promise<ProductOrder> => {
  try {
    const formData = new FormData();
    formData.append("kind", kind);
    formData.append("file", file);
    const response = await api.post<ProductOrder>(
      `product-orders/${orderId}/documents`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to upload document");
    throw error;
  }
};

export const deleteProductOrderDocument = async (
  orderId: string,
  documentId: string,
): Promise<ProductOrder> => {
  try {
    const response = await api.delete<ProductOrder>(
      `product-orders/${orderId}/documents/${documentId}`,
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to delete document");
    throw error;
  }
};

// Services APIs
export type SiteServiceRecordStatus = "ACTIVE" | "DISABLED";

export interface SiteServiceType {
  id: string;
  name: string;
  iconUrl: string | null;
  status: SiteServiceRecordStatus;
  createdAt: string;
  updatedAt: string;
}

export interface GetSiteServiceTypesResponse {
  data: SiteServiceType[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface CreateSiteServiceTypePayload {
  name: string;
  iconUrl?: string;
  status?: SiteServiceRecordStatus;
}

export interface UpdateSiteServiceTypePayload {
  name?: string;
  iconUrl?: string;
  status?: SiteServiceRecordStatus;
}

export const getSiteServiceTypes = async (
  page = 1,
  limit = 10,
  search = "",
  status?: SiteServiceRecordStatus,
): Promise<GetSiteServiceTypesResponse> => {
  try {
    const params: {
      page: number;
      limit: number;
      search: string;
      status?: SiteServiceRecordStatus;
    } = { page, limit, search };
    if (status) params.status = status;
    const response = await api.get<GetSiteServiceTypesResponse>(
      "site-service-types",
      { params },
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to get service types");
    throw error;
  }
};

export const getSiteServiceType = async (
  id: string,
): Promise<SiteServiceType> => {
  try {
    const response = await api.get<SiteServiceType>(`site-service-types/${id}`);
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to get service type");
    throw error;
  }
};

export const createSiteServiceType = async (
  data: CreateSiteServiceTypePayload,
): Promise<SiteServiceType> => {
  try {
    const response = await api.post<SiteServiceType>("site-service-types", data);
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to create service type");
    throw error;
  }
};

export const updateSiteServiceType = async (
  id: string,
  data: UpdateSiteServiceTypePayload,
): Promise<SiteServiceType> => {
  try {
    const response = await api.patch<SiteServiceType>(
      `site-service-types/${id}`,
      data,
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to update service type");
    throw error;
  }
};

export const deleteSiteServiceType = async (id: string): Promise<void> => {
  try {
    await api.delete(`site-service-types/${id}`);
  } catch (error) {
    handleAxiosError(error, "Failed to delete service type");
    throw error;
  }
};

export const uploadSiteServiceTypeIcon = async (
  serviceTypeId: string,
  file: File,
): Promise<SiteServiceType> => {
  try {
    const formData = new FormData();
    formData.append("file", file);
    const response = await api.post<SiteServiceType>(
      `site-service-types/${serviceTypeId}/icon`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to upload service type icon");
    throw error;
  }
};

export const removeSiteServiceTypeIcon = async (
  serviceTypeId: string,
): Promise<SiteServiceType> => {
  try {
    const response = await api.delete<SiteServiceType>(
      `site-service-types/${serviceTypeId}/icon`,
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to remove service type icon");
    throw error;
  }
};

export interface SiteServiceRequestUser {
  id: string;
  fullName?: string;
  email?: string;
  phoneNumber?: string;
}

export interface SiteServiceRequestType {
  id: string;
  name: string;
  iconUrl: string | null;
  status: SiteServiceRecordStatus;
}

export interface SiteServiceRequest {
  id: string;
  reference: string;
  userId: string;
  user?: SiteServiceRequestUser;
  serviceTypeId: string;
  serviceType?: SiteServiceRequestType;
  invoices: LinkedOrderInvoice[];
  quotations?: LinkedOrderQuotation[];
  quotation?: LinkedOrderQuotation | null;
  attachments?: EntityAttachment[];
  documents?: ExternalDocument[];
  uploadedInvoices?: ExternalDocument[];
  uploadedQuotations?: ExternalDocument[];
  createdAt: string;
  updatedAt: string;
}

export interface GetSiteServiceRequestsResponse {
  data: SiteServiceRequest[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export const getSiteServiceRequests = async (
  page = 1,
  limit = 10,
  search = "",
  filters?: {
    userId?: string;
    serviceTypeName?: string;
    invoiceId?: string;
    quotationId?: string;
  },
): Promise<GetSiteServiceRequestsResponse> => {
  try {
    const params: Record<string, string | number> = { page, limit, search };
    if (filters?.userId) params.userId = filters.userId;
    if (filters?.serviceTypeName) params.serviceTypeName = filters.serviceTypeName;
    if (filters?.invoiceId) params.invoiceId = filters.invoiceId;
    if (filters?.quotationId) params.quotationId = filters.quotationId;
    const response = await api.get<GetSiteServiceRequestsResponse>(
      "site-service-requests",
      { params },
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to get service requests");
    throw error;
  }
};

export const getSiteServiceRequest = async (
  id: string,
): Promise<SiteServiceRequest> => {
  try {
    const response = await api.get<SiteServiceRequest>(
      `site-service-requests/${id}`,
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to get service request");
    throw error;
  }
};

export const setSiteServiceRequestInvoices = async (
  requestId: string,
  invoiceIds: string[],
): Promise<SiteServiceRequest> => {
  try {
    const response = await api.patch<SiteServiceRequest>(
      `site-service-requests/${requestId}/invoices`,
      { invoiceIds },
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to update linked invoices");
    throw error;
  }
};

export const setSiteServiceRequestQuotations = async (
  requestId: string,
  quotationIds: string[],
): Promise<SiteServiceRequest> => {
  try {
    const response = await api.patch<SiteServiceRequest>(
      `site-service-requests/${requestId}/quotations`,
      { quotationIds },
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to update linked quotations");
    throw error;
  }
};

export const uploadSiteServiceRequestDocument = async (
  requestId: string,
  kind: ExternalDocumentKind,
  file: File,
): Promise<SiteServiceRequest> => {
  try {
    const formData = new FormData();
    formData.append("kind", kind);
    formData.append("file", file);
    const response = await api.post<SiteServiceRequest>(
      `site-service-requests/${requestId}/documents`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to upload document");
    throw error;
  }
};

export const deleteSiteServiceRequestDocument = async (
  requestId: string,
  documentId: string,
): Promise<SiteServiceRequest> => {
  try {
    const response = await api.delete<SiteServiceRequest>(
      `site-service-requests/${requestId}/documents/${documentId}`,
    );
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to delete document");
    throw error;
  }
};

// App settings (mobile)
export interface AppSettings {
  id: string;
  singletonKey: string;
  iosLatestVersion: string;
  iosMinSupportedVersion: string;
  iosForceUpdate: boolean;
  androidLatestVersion: string;
  androidMinSupportedVersion: string;
  androidForceUpdate: boolean;
  maintenanceMode: boolean;
  iosStoreUrl: string | null;
  androidStoreUrl: string | null;
  supportPhoneNumber: string | null;
  supportEmail: string | null;
  supportWhatsApp: string | null;
  supportHours: string | null;
  maintenanceMessage: string | null;
  privacyPolicyUrl: string | null;
  termsOfServiceUrl: string | null;
  websiteUrl: string | null;
  facebookUrl: string | null;
  instagramUrl: string | null;
  twitterUrl: string | null;
  linkedInUrl: string | null;
  aboutText: string | null;
  createdAt: string;
  updatedAt: string;
}

export type UpdateAppSettingsPayload = {
  iosLatestVersion?: string;
  iosMinSupportedVersion?: string;
  iosForceUpdate?: boolean;
  androidLatestVersion?: string;
  androidMinSupportedVersion?: string;
  androidForceUpdate?: boolean;
  maintenanceMode?: boolean;
  iosStoreUrl?: string | null;
  androidStoreUrl?: string | null;
  supportPhoneNumber?: string | null;
  supportEmail?: string | null;
  supportWhatsApp?: string | null;
  supportHours?: string | null;
  maintenanceMessage?: string | null;
  privacyPolicyUrl?: string | null;
  termsOfServiceUrl?: string | null;
  websiteUrl?: string | null;
  facebookUrl?: string | null;
  instagramUrl?: string | null;
  twitterUrl?: string | null;
  linkedInUrl?: string | null;
  aboutText?: string | null;
};

export const getAppSettings = async (): Promise<AppSettings> => {
  try {
    const response = await api.get<AppSettings>("settings");
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to get app settings");
    throw error;
  }
};

export const updateAppSettings = async (
  data: UpdateAppSettingsPayload,
): Promise<AppSettings> => {
  try {
    const response = await api.patch<AppSettings>("settings", data);
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to update app settings");
    throw error;
  }
};

// Attachment types
export type AttachmentContext = "PRODUCT_ORDER" | "SITE_SERVICE_REQUEST";
export type AttachmentTypeStatus = "ACTIVE" | "DISABLED";

export interface AttachmentType {
  id: string;
  name: string;
  context: AttachmentContext;
  status: AttachmentTypeStatus;
  allowedExtensions: string;
  maxFileSizeMb: number;
  maxFilesPerEntity: number;
  description: string | null;
  sortOrder: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateAttachmentTypePayload {
  name: string;
  context: AttachmentContext;
  status?: AttachmentTypeStatus;
  allowedExtensions: string;
  maxFileSizeMb?: number;
  maxFilesPerEntity?: number;
  description?: string;
  sortOrder?: number;
}

export interface UpdateAttachmentTypePayload {
  name?: string;
  context?: AttachmentContext;
  status?: AttachmentTypeStatus;
  allowedExtensions?: string;
  maxFileSizeMb?: number;
  maxFilesPerEntity?: number;
  description?: string | null;
  sortOrder?: number | null;
}

export const getAttachmentTypes = async (
  context?: AttachmentContext,
): Promise<AttachmentType[]> => {
  try {
    const params = context ? { context } : undefined;
    const response = await api.get<AttachmentType[] | { data: AttachmentType[] }>(
      "attachment-types",
      { params },
    );
    return Array.isArray(response.data) ? response.data : (response.data.data ?? []);
  } catch (error) {
    handleAxiosError(error, "Failed to get attachment types");
    throw error;
  }
};

export const createAttachmentType = async (
  data: CreateAttachmentTypePayload,
): Promise<AttachmentType> => {
  try {
    const response = await api.post<AttachmentType>("attachment-types", data);
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to create attachment type");
    throw error;
  }
};

export const updateAttachmentType = async (
  id: string,
  data: UpdateAttachmentTypePayload,
): Promise<AttachmentType> => {
  try {
    const response = await api.patch<AttachmentType>(`attachment-types/${id}`, data);
    return response.data;
  } catch (error) {
    handleAxiosError(error, "Failed to update attachment type");
    throw error;
  }
};

export const deleteAttachmentType = async (id: string): Promise<void> => {
  try {
    await api.delete(`attachment-types/${id}`);
  } catch (error) {
    handleAxiosError(error, "Failed to delete attachment type");
    throw error;
  }
};

export default api;
